/* Forms: Klaviyo (Client API) for the panel, reminders, capture and SMS; a webhook (Zapier, HubSpot, the CRM)
   for quotes, company accounts, reseller applications and claims; Shopify's contact form posts natively.
   Every form shows its confirmation inline except the capture form, which navigates to its thank-you page.
   The forms that reach a person (lead-form.liquid: quotes, company accounts, resellers, claims, contact) also open a
   pop-up with the same confirmation once they are sent, so nobody wonders whether the click worked (15 October).
   A field wrapper with data-show-if="<checkbox name>" shows, and submits, only while that box is ticked. */
import { $$, track, utm, onReady } from '@theme/utils';

const T = () => window.Theme;

function fields(form) {
  const data = {};
  new FormData(form).forEach((v, k) => { if (k.startsWith('_')) return; if (data[k] !== undefined) data[k] = [].concat(data[k], v); else data[k] = v; });
  return data;
}
function setState(form, state, msg) {
  const btn = form.querySelector('[type=submit]');
  if (btn) { if (state === 'busy') btn.setAttribute('aria-busy', 'true'); else btn.removeAttribute('aria-busy'); }
  const err = form.querySelector('.form-err');
  if (err) { err.classList.toggle('show', state === 'error'); if (state === 'error') err.textContent = msg || T().strings.formError; }
  if (state === 'done') { form.classList.add('is-done'); const ok = form.querySelector('.ok'); if (ok) ok.focus && ok.setAttribute('tabindex', '-1'); }
}

async function klaviyo(form) {
  const companyId = form.dataset.company || T().settings.klaviyoCompanyId;
  const listId = form.dataset.list;
  const d = fields(form);
  const email = (d.email || '').trim(), phone = (d.phone || d.mobile || '').trim();
  if (!companyId) throw new Error('Klaviyo company id missing');
  const props = {};
  $$('[data-prop]', form).forEach((el) => {
    if (el.disabled) return;
    if (el.type === 'checkbox') { if (el.checked) props[el.dataset.prop] = true; }
    else if (el.value) props[el.dataset.prop] = el.value;
  });
  const subscriptions = {};
  if (email) subscriptions.email = { marketing: { consent: 'SUBSCRIBED' } };
  const smsBox = form.querySelector('[data-sms-consent]');
  if (phone && (!smsBox || smsBox.checked)) subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };
  const attrs = { properties: props, subscriptions };
  if (email) attrs.email = email;
  if (phone) attrs.phone_number = phone.startsWith('+') ? phone : '+1' + phone.replace(/\D/g, '');
  const body = { data: { type: 'subscription', attributes: { custom_source: form.dataset.source || 'NOCRY site', profile: { data: { type: 'profile', attributes: attrs } } } } };
  if (listId) body.data.relationships = { list: { data: { type: 'list', id: listId } } };
  const res = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', revision: '2025-01-15' }, body: JSON.stringify(body),
  });
  if (!res.ok && res.status !== 202) { const j = await res.json().catch(() => ({})); throw new Error((j.errors && j.errors[0] && j.errors[0].detail) || 'Klaviyo error'); }
}

async function webhook(form) {
  const url = form.dataset.endpoint || T().settings.formsWebhookUrl;
  if (!url) throw new Error('No endpoint');
  const d = fields(form);
  const meta = { form: form.dataset.name || form.id || 'form', tag: form.dataset.tag || '', page: location.href, ...utm() };
  const hasFile = Array.from(form.querySelectorAll('input[type=file]')).some((i) => i.files && i.files.length);
  let res;
  if (hasFile) {
    /* photos (warranty claims) go as multipart; Zapier, Make and most CRM endpoints accept it */
    const fd = new FormData(form);
    Array.from(fd.keys()).filter((k) => k.startsWith('_')).forEach((k) => fd.delete(k));
    Object.entries(meta).forEach(([k, v]) => fd.append(k, v));
    res = await fetch(url, { method: 'POST', body: fd });
  } else {
    res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...meta, ...d }) });
  }
  if (!res.ok) throw new Error('Webhook error ' + res.status);
}

/* the "sent" pop-up for the forms that reach a person. Built once, on first use; the .mdl modal from nocry-base.css with
   a heading, the form's own success line and one button. OK, Escape or a tap outside the box closes it, and focus
   goes back to the success line in the form. Its few rules live here with it: move them into nocry-base.css when that
   file is next rebuilt */
const SENT_TITLES = { company_account: 'Application sent', reseller: 'Application sent', warranty_claim: 'Claim sent' };
let sentBox = null;
function sentModal() {
  if (sentBox) return sentBox;
  const css = document.createElement('style');
  css.textContent = '.sentbox{align-items:center;padding:16px 16px max(16px,env(safe-area-inset-bottom))}.sentbox .box{width:min(100%,440px);padding:22px 20px 24px}'
    + '.sentbox h3{display:flex;align-items:center;gap:10px;margin-bottom:10px}'
    + '.sentbox h3 i{font-style:normal;color:var(--red)}'
    + '.sentbox .box p{color:var(--ink);font-size:1rem;margin-bottom:18px}'
    + '.sentbox .btn{min-width:140px}';
  document.head.appendChild(css);
  sentBox = document.createElement('div');
  sentBox.className = 'mdl sentbox';
  sentBox.setAttribute('role', 'dialog');
  sentBox.setAttribute('aria-modal', 'true');
  sentBox.setAttribute('aria-labelledby', 'sentbox-h');
  sentBox.innerHTML = '<div class="box"><h3 class="h-head" id="sentbox-h"><i aria-hidden="true">✓</i><span></span></h3>'
    + '<p data-sent-msg></p><button class="btn btn-dark" type="button" data-sent-close>OK</button></div>';
  document.body.appendChild(sentBox);
  sentBox.addEventListener('click', (e) => { if (e.target === sentBox || e.target.closest('[data-sent-close]')) closeSent(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && sentBox.classList.contains('open')) closeSent(); });
  return sentBox;
}
let sentReturn = null;
function closeSent() {
  sentBox.classList.remove('open');
  if (sentReturn && sentReturn.focus) sentReturn.focus({ preventScroll: true });
  sentReturn = null;
}
export function confirmSent(form) {
  if (!form || !form.classList.contains('lead-form')) return;
  const ok = form.querySelector('.ok');
  if (!ok || !ok.textContent.trim()) return;
  const box = sentModal();
  box.querySelector('#sentbox-h span').textContent = SENT_TITLES[form.dataset.name] || 'Sent';
  box.querySelector('[data-sent-msg]').innerHTML = ok.innerHTML;
  sentReturn = ok;
  box.classList.add('open');
  box.querySelector('[data-sent-close]').focus({ preventScroll: true });
}

/* conditional fields: hidden and disabled (so they neither validate nor submit) until their checkbox is ticked */
function conditionals(form) {
  $$('[data-show-if]', form).forEach((w) => {
    const n = w.dataset.showIf;
    const box = form.querySelector(`input[type=checkbox][name="${n}"], input[type=checkbox][name="contact[${n}]"]`);
    const on = !!(box && box.checked);
    w.hidden = !on;
    $$('input, select, textarea', w).forEach((el) => { el.disabled = !on; });
  });
}
export function initForms(root = document) { $$('form[data-form]', root).forEach(conditionals); }
document.addEventListener('change', (e) => {
  const form = e.target.closest && e.target.closest('form[data-form]');
  if (form && e.target.type === 'checkbox') conditionals(form);
});
/* After Shopify's contact form reloads the page: keep the success or error line only in the form that was sent,
   and bring that form into view (the reload lands at the top of the page). */
const SENT_KEY = 'nocry-form-sent';
function showPosted() {
  if (!document.querySelector('form[data-form="shopify"] [data-posted]')) return;
  let id = null;
  try { id = sessionStorage.getItem(SENT_KEY); sessionStorage.removeItem(SENT_KEY); } catch (err) { /* storage blocked */ }
  const sent = id && document.getElementById(id);
  if (!sent) return;
  $$('form[data-form="shopify"]').forEach((f) => { if (f !== sent) $$('[data-posted]', f).forEach((n) => n.remove()); });
  const msg = sent.querySelector('[data-posted]');
  sent.scrollIntoView({ block: 'start' });
  if (msg) { msg.setAttribute('tabindex', '-1'); msg.setAttribute('role', msg.classList.contains('ok') ? 'status' : 'alert'); msg.focus({ preventScroll: true }); }
  if (msg && msg.classList.contains('ok')) confirmSent(sent);
}
onReady(() => { initForms(); showPosted(); });
document.addEventListener('shopify:section:load', (e) => initForms(e.target));

document.addEventListener('submit', async (e) => {
  const form = e.target;
  if (!(form instanceof HTMLFormElement) || !form.dataset.form) return;
  const kind = form.dataset.form;
  if (kind === 'shopify') {
    /* remember which form was sent: Shopify's "sent" state after the reload covers every contact form on the page */
    try { sessionStorage.setItem(SENT_KEY, form.id); } catch (err) { /* private mode: both forms keep the message */ }
    track(form.dataset.event || 'form_submitted', { form: form.dataset.name || '' });
    return;
  }
  e.preventDefault();
  const hp = form.querySelector('.hp input'); if (hp && hp.value) return;
  if (!form.reportValidity()) return;
  setState(form, 'busy');
  try {
    if (kind === 'klaviyo') await klaviyo(form);
    else if (kind === 'webhook') await webhook(form);
    else if (kind === 'none') { /* demo */ }
    track(form.dataset.event || 'form_submitted', { form: form.dataset.name || '', page: document.body.dataset.page || location.pathname, ...utm() });
    if (form.dataset.next) {
      /* the thank-you page keeps the ad's parameters (utm_content, h) so its Lead event reads in the same report */
      const next = form.dataset.next, q = location.search.replace(/^\?/, '');
      location.href = q && !/[?&]utm_/.test(next) ? next + (next.includes('?') ? '&' : '?') + q : next;
      return;
    }
    setState(form, 'done');
    confirmSent(form);
  } catch (err) {
    setState(form, 'error', err.message);
  }
});
